import { ColorPicker } from "./ColorPicker";

/**
 * VisualElementControl
 * One editable card per Big Screen element. The exposed fields depend on
 * `kind` (from the schema):
 *   text       → color + size + X + Y
 *   photo      → bg_color + border_color + size + X + Y
 *   flag       → size + X + Y (no color, drapeau)
 *   highlight  → color + glow (0..100%) + size + X + Y
 */
export const VisualElementControl = ({
  label,
  preview,
  value,
  onChange,
  testKey,
  kind = "text",
  sizeRange = [0.3, 3, 0.05],
}) => {
  const v = value || {};
  const size = v.size ?? 1;
  const ox = v.offset_x ?? 0;
  const oy = v.offset_y ?? 0;
  const [sMin, sMax, sStep] = sizeRange;

  const patch = (part) => onChange({ ...v, ...part });

  const previewNode = preview ? (
    <span
      className="px-3 py-1 rounded whitespace-nowrap"
      style={{
        color: v.color || "#ffffff",
        background: kind === "text_bg" ? (v.bg_color || "rgba(0,0,0,0.6)") : "rgba(0,0,0,0.6)",
        fontSize: `${Math.min(size, 2) * 1.1}rem`,
        fontWeight: 700,
        lineHeight: 1,
        transform: `translate(${Math.max(-40, Math.min(40, ox / 4))}px, ${Math.max(-20, Math.min(20, oy / 4))}px)`,
        border: kind === "highlight" ? `2px solid ${v.color || "#facc15"}` : undefined,
        boxShadow: kind === "highlight" ? `0 0 24px ${v.color || "#facc15"}${Math.round(((v.glow ?? 0.55) * 255)).toString(16).padStart(2, "0")}` : undefined,
      }}
    >
      {preview}
    </span>
  ) : null;

  return (
    <div className="rounded-xl border border-border bg-card/60 p-4 space-y-3" data-testid={`viz-${testKey}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h4 className="text-base font-semibold text-foreground">{label}</h4>
          {previewNode}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {(kind === "text" || kind === "text_bg" || kind === "highlight") && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Color</span>
              <ColorPicker
                value={v.color || "#ffffff"}
                onChange={(c) => patch({ color: c })}
                testId={`viz-${testKey}-color`}
                ariaLabel={`${label} color`}
              />
            </div>
          )}
          {(kind === "text" || kind === "text_bg" || kind === "highlight") && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none cursor-pointer" title="Drop-shadow readability on any background (white-on-white protection)">
              <input
                type="checkbox"
                checked={v.shadow !== false}
                onChange={(e) => patch({ shadow: e.target.checked })}
                className="accent-primary h-4 w-4"
                data-testid={`viz-${testKey}-shadow`}
              />
              <span>Shadow</span>
            </label>
          )}
          {kind === "text_bg" && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">BG</span>
              <ColorPicker
                value={v.bg_color || "#000000"}
                onChange={(c) => patch({ bg_color: c })}
                testId={`viz-${testKey}-bg`}
                ariaLabel={`${label} background`}
              />
            </div>
          )}
          {kind === "photo" && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">BG (no photo)</span>
                <ColorPicker
                  value={v.bg_color || "#7c3aed"}
                  onChange={(c) => patch({ bg_color: c })}
                  testId={`viz-${testKey}-bg`}
                  ariaLabel={`${label} background`}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Border</span>
                <ColorPicker
                  value={v.border_color || "#ffffff"}
                  onChange={(c) => patch({ border_color: c })}
                  testId={`viz-${testKey}-border`}
                  ariaLabel={`${label} border`}
                />
              </div>
              {v.color !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Initial</span>
                  <ColorPicker
                    value={v.color || "#ffffff"}
                    onChange={(c) => patch({ color: c })}
                    testId={`viz-${testKey}-color`}
                    ariaLabel={`${label} initial color`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="block text-xs text-muted-foreground mb-1">Size × {size.toFixed(2)}</span>
          <input
            type="range"
            min={sMin}
            max={sMax}
            step={sStep}
            value={size}
            onChange={(e) => patch({ size: parseFloat(e.target.value) })}
            className="w-full accent-primary"
            data-testid={`viz-${testKey}-size`}
          />
        </label>
        <label className="block">
          <span className="block text-xs text-muted-foreground mb-1">Offset X ({ox}px)</span>
          <input
            type="range"
            min={-300}
            max={300}
            step={1}
            value={ox}
            onChange={(e) => patch({ offset_x: parseInt(e.target.value, 10) })}
            className="w-full accent-primary"
            data-testid={`viz-${testKey}-x`}
          />
        </label>
        <label className="block">
          <span className="block text-xs text-muted-foreground mb-1">Offset Y ({oy}px)</span>
          <input
            type="range"
            min={-300}
            max={300}
            step={1}
            value={oy}
            onChange={(e) => patch({ offset_y: parseInt(e.target.value, 10) })}
            className="w-full accent-primary"
            data-testid={`viz-${testKey}-y`}
          />
        </label>
        {kind === "photo" && (
          <>
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">Width × {(v.width ?? 1).toFixed(2)}</span>
              <input
                type="range"
                min={0.3}
                max={2.5}
                step={0.02}
                value={v.width ?? 1}
                onChange={(e) => patch({ width: parseFloat(e.target.value) })}
                className="w-full accent-primary"
                data-testid={`viz-${testKey}-width`}
              />
            </label>
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">Height × {(v.height ?? 1).toFixed(2)}</span>
              <input
                type="range"
                min={0.3}
                max={2.5}
                step={0.02}
                value={v.height ?? 1}
                onChange={(e) => patch({ height: parseFloat(e.target.value) })}
                className="w-full accent-primary"
                data-testid={`viz-${testKey}-height`}
              />
            </label>
          </>
        )}
        {kind === "highlight" && (
          <label className="block sm:col-span-3">
            <span className="block text-xs text-muted-foreground mb-1">Halo intensity ({Math.round((v.glow ?? 0.55) * 100)}%)</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={v.glow ?? 0.55}
              onChange={(e) => patch({ glow: parseFloat(e.target.value) })}
              className="w-full accent-primary"
              data-testid={`viz-${testKey}-glow`}
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default VisualElementControl;
